import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { useDebounce } from '@/hooks/use-debounce'; // Adjust path as needed
import { cn } from "@/lib/utils"; // Assuming you have shadcn's utils
import FullCalendar from '@fullcalendar/react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { AlertTriangle, Check, ChevronsUpDown, Clock2 } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import { Checkbox } from '~/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { Switch } from '~/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '~/components/ui/tabs';
import LocalDateTime from "~/lib/localdatetime";
import { serializeConflictsForLog } from "~/lib/schedule-entry-conflicts";
import { useAppointmentsCalendarStore } from '~/stores/appointments-calendar-store';
import { EquipmentScheduleEntryType } from '~/types/server-types/EquipmentScheduleEntryType';
import { PractitionerScheduleEntryBlockedType } from "~/types/server-types/PractitionerScheduleEntryBlockedType";
import { PractitionerScheduleEntryType } from '~/types/server-types/PractitionerScheduleEntryType';
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { useDebounceFunction } from "~/hooks/use-debounce-function";
import { equipment, practitioner, PractitionerScheduleEntry } from "@server/prisma-generated/client";
import { PractitionerNameWithCategory } from "~/components/practitioners/PractitionerNameWithCategory";
import { useUserStore } from "~/stores/user-store";
import { UserRole } from "~/types/server-types/UserRole";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Define Practitioner type if not imported directly or needs modification
type Practitioner = Pick<practitioner, 'id' | 'firstName' | 'lastName' | 'title' | 'type' | 'categoryId'> & { categoryName?: string | null; ignoreAvailabilities?: boolean | null };
type Equipment = Pick<equipment, 'id' | 'name'>

const formatDate = (date: dayjs.Dayjs | null | undefined): string => {
    return date ? date.format('YYYY-MM-DD') : '';
};

const formatTime = (date: dayjs.Dayjs | null | undefined): string => {
    return date ? date.format('HH:mm') : '';
};

const parseDate = (dateStr: string, existingTime: dayjs.Dayjs | null, tz: string): dayjs.Dayjs | null => {
    if (!dateStr) return null;
    const time = existingTime ? existingTime.format('HH:mm') : '00:00';
    try {
        return dayjs.tz(`${dateStr} ${time}`, tz);
    } catch {
        return dayjs(`${dateStr} ${time}`);
    }
};

const parseTime = (timeStr: string, existingDate: dayjs.Dayjs | null, tz: string): dayjs.Dayjs | null => {
    if (!timeStr || !existingDate) return null;
    const date = existingDate.format('YYYY-MM-DD');
    try {
        return dayjs.tz(`${date} ${timeStr}`, tz);
    } catch {
        return dayjs(`${date} ${timeStr}`);
    }
};

interface CalendarScheduleTimeBlockEntryProps {
    calendarRef?: React.RefObject<FullCalendar | null>;
    userTimezone: string;
    selectedSlot?: { start: dayjs.Dayjs, end: dayjs.Dayjs };
    triggerButtonLabel?: string;
    triggerButtonProps?: React.ComponentProps<typeof Button>;
    onSuccess?: (newEntry: PractitionerScheduleEntry) => void;
}

export const CalendarScheduleTimeBlockEntry = ({
    calendarRef,
    userTimezone,
    selectedSlot,
    triggerButtonLabel = "Block Time Slot",
    triggerButtonProps,
    onSuccess,
}: CalendarScheduleTimeBlockEntryProps) => {
    const { selectedStore, selectedDateRange } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
            selectedDateRange: state.selectedDateRange,
        }))
    );

    console.log("CalendarScheduleTimeBlockEntry");
    const userRole = useUserStore(state => state.user?.role);
    const canManageShiftEntries = userRole === UserRole.ADMIN || userRole === UserRole.OPERATOR;

    const defaultStartDate = useMemo(() => {
        const date = new Date(selectedDateRange.startDate.getDisplayDateTime());
        date.setHours(10);
        date.setMinutes(0);
        return date;
    }, [selectedDateRange]);

    const [isOpen, setIsOpen] = useState(false);
    const trpcUtils = trpc.useUtils();

    // --- Search State ---
    const [dataSearchOpen, setDataSearchOpen] = useState(false);
    const [selectedDataObjects, setSelectedDataObjects] = useState<Practitioner[] | Equipment[]>([]);
    const [dataSearchQuery, setDataSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(dataSearchQuery, 300); // Debounce API calls

    // --- Form State ---
    const [description, setDescription] = useState('');
    const [startTimeObj, setStartTimeObj] = useState<dayjs.Dayjs | null>(null);
    const [endTimeObj, setEndTimeObj] = useState<dayjs.Dayjs | null>(null);
    const [blockedType, setBlockedType] = useState<PractitionerScheduleEntryBlockedType | null>(null);

    // --- Recurrence State ---
    const [isRecurring, setIsRecurring] = useState(false);
    const [recurrenceEndDateObj, setRecurrenceEndDateObj] = useState<dayjs.Dayjs | null>(null);

    const [frequency, setFrequency] = useState<'DAILY' | 'WEEKLY' | 'MONTHLY'>('WEEKLY'); // Default
    const [interval, setInterval] = useState<number>(1); // Default: Every 1 day/week/month
    const [weekdays, setWeekdays] = useState<Record<string, boolean>>({ // For weekly selection
        MO: false, TU: false, WE: false, TH: false, FR: false, SA: false, SU: false,
    });

    // *** We will now DERIVE the recurrenceRule using useEffect ***
    const [derivedRecurrenceRule, setDerivedRecurrenceRule] = useState<string>('');

    const dayjsToRruleWeekdays: { [key: number]: string } = {
        0: 'SU', 1: 'MO', 2: 'TU', 3: 'WE', 4: 'TH', 5: 'FR', 6: 'SA'
    };

    useEffect(() => {
        if (!isRecurring) {
            setDerivedRecurrenceRule(''); // Clear rule if not recurring
            // Optionally reset builder state here too for cleanliness
            // setFrequency('WEEKLY');
            // setInterval(1);
            // setWeekdays({ MO: false, ... });
            return;
        }

        let parts: string[] = [];
        parts.push(`FREQ=${frequency}`);

        if (interval > 1) {
            parts.push(`INTERVAL=${interval}`);
        }

        if (frequency === 'WEEKLY') {
            const selectedDays = Object.entries(weekdays)
                .filter(([, isSelected]) => isSelected)
                .map(([day]) => day);

            if (selectedDays.length > 0) {
                parts.push(`BYDAY=${selectedDays.join(',')}`);
            } else {
                // Handle case where no days are selected - maybe default to start day?
                // Or rely on validation. For now, generate potentially invalid rule & validate later.
            }
        }

        // For MONTHLY, we implicitly rely on the DTSTART day by default (no BYMONTHDAY/BYDAY needed for simple case)

        setDerivedRecurrenceRule(parts.join(';'));

    }, [isRecurring, frequency, interval, weekdays]);


    // Effect to set default weekday based on start time when switching to weekly/recurring
    useEffect(() => {
        if (isRecurring && frequency === 'WEEKLY' && startTimeObj) {
            // Only set default if *no* days are currently selected (avoid overriding user choice)
            const anyDaySelected = Object.values(weekdays).some(selected => selected);
            if (!anyDaySelected) {
                const startDay = dayjsToRruleWeekdays[startTimeObj.day()]; // day() is locale-aware, 0=Sun, 1=Mon...
                if (startDay) {
                    setWeekdays(prev => ({ ...prev, [startDay]: true }));
                }
            }
        }
        // If switching away from weekly, clear weekdays? Optional, maybe keep selection.
        // else if (!isRecurring || frequency !== 'WEEKLY') {
        //     setWeekdays({ MO: false, TU: false, WE: false, TH: false, FR: false, SA: false, SU: false });
        // }

    }, [isRecurring, frequency, startTimeObj]); // Rerun if start time changes while weekly is active

    const [dataType, setDataType] = useState<'practitioners' | 'equipment'>('practitioners');
    const dataTypeDisplayName = useMemo(() => {
        if (dataType === 'practitioners') {
            return 'Behandler'
        } else if (dataType === 'equipment') {
            return 'Gerät'
        }
        return 'N/A'
    }, [dataType]);

    const dataObjectDisplayName = useMemo(() => {
        if (selectedDataObjects.length === 0) {
            return null;
        }
        if (dataType === 'practitioners') {
            const practitioners = selectedDataObjects as Practitioner[];
            return practitioners.map((p) => (
                <PractitionerNameWithCategory
                    key={p.id}
                    firstName={p.firstName}
                    lastName={p.lastName}
                    categoryName={p.categoryName}
                />
            )).reduce<React.ReactNode[]>((acc, node, index) => {
                if (index > 0) acc.push(<span key={`sep-${index}`}>, </span>);
                acc.push(node);
                return acc;
            }, []);
        } else if (dataType === 'equipment') {
            const equipments = selectedDataObjects as Equipment[]
            return equipments.map((equipment) => equipment.name).join(", ");
        }
        return null
    }, [selectedDataObjects, dataType]);

    useEffect(() => {
        // Reset practitioner selection
        setSelectedDataObjects([]);
        setDataSearchQuery('');

        // Reset time and description
        const defaultStart = selectedSlot?.start ?? dayjs(defaultStartDate).tz(userTimezone).startOf('hour').add(1, 'hour');
        const defaultEnd = selectedSlot?.end ?? defaultStart.add(1, 'hour');
        setStartTimeObj(defaultStart);
        setEndTimeObj(defaultEnd);
        setDescription('');
    }, [dataType]);

    // --- tRPC ---
    // Hohes Limit, damit "Alle auswählen" wirklich alle Behandler des Stores erfasst
    // und nicht nur die ersten 10 Suchtreffer (sonst fehlen bei >10 Behandlern dauerhaft welche).
    const practitionerSearch = trpc.practitioners.search.useQuery(
        { query: debouncedSearchQuery, storeId: selectedStore?.id, limit: 100 },
        {
            enabled: dataType === 'practitioners' && dataSearchOpen, // Only fetch when query exists and popover is open
            staleTime: 30000, // Cache results briefly
        }
    );

    const equipmentSearch = trpc.equipment.search.useQuery(
        { query: debouncedSearchQuery, storeId: selectedStore?.id, limit: 100 },
        {
            enabled: dataType === 'equipment' && dataSearchOpen, // Only fetch when query exists and popover is open
            staleTime: 30000, // Cache results briefly
        }
    )

    const createPractitionerScheduleEntryMutation = trpc.practitioners.createScheduleEntry.useMutation({
        onSuccess: (data) => {
            toast.success("Success", { description: "Blocked time slot created." });
            // Invalidate relevant queries - potentially all schedule entries or just the specific practitioner's
            if (selectedDataObjects.length > 0) {
                selectedDataObjects.forEach((practitioner) => {
                    trpcUtils.practitioners.getScheduleEntries.invalidate({ practitionerId: practitioner.id });
                })
            } else {
                // Invalidate all if you can't determine the specific practitioner? Or rely on higher-level refresh.
                // Consider invalidating a broader query if necessary.
                trpcUtils.practitioners.getScheduleEntries.invalidate(); // Example: might need adjustment based on your query keys
            }

            trpcUtils.appointments.getAppointmentsForScheduler.refetch();
            setIsOpen(false);
            onSuccess?.(data as any as PractitionerScheduleEntry);
        },
        onError: (err) => {
            console.error('Failed to create blocked entry:', err);
            toast.error("Error", { description: `Failed to create entry: ${err.message}` });
        },
    });

    const createEquipmentScheduleEntryMutation = trpc.equipment.createScheduleEntry.useMutation({
        onSuccess: (data) => {
            toast.success("Success", { description: "Blocked time slot created." });
            trpcUtils.appointments.getAppointmentsForScheduler.refetch();
            setIsOpen(false);
            onSuccess?.(data as any as PractitionerScheduleEntry); // Optional external callback
        },
        onError: (err) => {
            console.error('Failed to create blocked entry:', err);
            toast.error("Error", { description: `Failed to create entry: ${err.message}` });
        },
    });

    // --- Effects ---

    // Effect to reset form state when dialog opens
    useEffect(() => {
        if (isOpen) {
            // Reset selected data type
            setDataType("practitioners");
            // Reset practitioner selection
            setSelectedDataObjects([]);
            setDataSearchQuery('');

            // Reset time and description
            const defaultStart = selectedSlot?.start ?? dayjs(defaultStartDate).tz(userTimezone).startOf('hour').add(1, 'hour');
            const defaultEnd = selectedSlot?.end ?? defaultStart.add(1, 'hour');
            setStartTimeObj(defaultStart);
            setEndTimeObj(defaultEnd);
            setDescription('');
            setBlockedType(null);
        }
    }, [isOpen, userTimezone, selectedSlot, defaultStartDate]);

    // Effect to update end time if start time changes (maintaining 1hr default duration unless prefilled)
    useEffect(() => {
        // Only adjust end time automatically if it wasn't explicitly prefilled by selectedSlot
        // and the start time is valid
        if (startTimeObj && !selectedSlot?.end && endTimeObj?.isSame(startTimeObj.subtract(1, 'hour'))) {
            // Check if the current end time seems to be the default derived one
            // This logic might need refinement based on exact behavior desired.
            // A simpler approach: always set end = start + 1hr if start changes *unless* selectedSlot.end exists.
            setEndTimeObj(startTimeObj.add(1, 'hour'));
        }
        // If only selectedSlot.start was provided, default end time should be start + 1hr
        else if (selectedSlot?.start && !selectedSlot.end && startTimeObj?.isSame(selectedSlot.start)) {
            setEndTimeObj(startTimeObj.add(1, 'hour'));
        }

    }, [startTimeObj, selectedSlot, endTimeObj]); // Dependency: endTimeObj to prevent infinite loops if user changes end time

    // Memoize practitioner list to avoid re-renders
    const practitionerOptions = useMemo(() => {
        const all = practitionerSearch.data?.data || [];
        return all.filter((p) => p.categoryId === 1 || p.categoryId === 2);
    }, [practitionerSearch.data]);
    const equipmentOptions = useMemo(() => equipmentSearch.data?.data || [], [equipmentSearch.data]);

    const searchIsLoading = useMemo(() => {
        return ((dataType === 'practitioners' && practitionerSearch.isLoading) || (dataType === 'equipment' && equipmentSearch.isLoading));
    }, [dataType, practitionerSearch, equipmentSearch]);

    const searchOptionsIsEmpty = useMemo(() => {
        return ((dataType === 'practitioners' && !practitionerOptions.length) || (dataType === 'equipment' && !equipmentOptions.length));
    }, [dataType, practitionerOptions, equipmentOptions])


    // --- Handlers ---
    const handleDataObjectSelectAll = () => {
        if (!canManageShiftEntries) return;
        if (dataType === 'practitioners') {
            // "Alle auswählen" soll nur echte Behandler auswählen –
            // MFAs und Empfangskräfte (Kategorien mit ignoreAvailabilities) werden ausgeschlossen.
            const treaterOptions = practitionerOptions.filter(
                (p) => p.type !== 'MFA' && !p.ignoreAvailabilities
            );
            setSelectedDataObjects(treaterOptions)
        } else if (dataType === 'equipment') {
            setSelectedDataObjects(equipmentOptions)
        }
    }

    const handleDataObjectSelect = (dataObject: Practitioner | Equipment) => {
        if (!canManageShiftEntries) return;
        const alreadySelected = selectedDataObjects.some((obj) => obj.id === dataObject.id);
        if (alreadySelected) {
            const newSelectedDataObjects = selectedDataObjects.filter((obj) => obj.id !== dataObject.id) as Practitioner[] | Equipment[];
            setSelectedDataObjects(newSelectedDataObjects);
        } else {
            const newSelectedDataObjects = [...selectedDataObjects, dataObject] as Practitioner[] | Equipment[];
            setSelectedDataObjects(newSelectedDataObjects);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!canManageShiftEntries) return;

        if (selectedDataObjects.length === 0) {
            toast.error("Error", { description: `Please select a ${dataTypeDisplayName}.` });
            return;
        }

        if (!selectedStore) {
            toast.error("Error", { description: "Please select a store in Termine." });
            return;
        }

        if (!startTimeObj || !endTimeObj) {
            toast.error("Error", { description: "Please provide valid start and end times." });
            return;
        }

        if (dataType === 'practitioners' && !blockedType) {
            toast.error("Error", { description: "Bitte wähle einen Blocker Grund aus." });
            return;
        }

        if (endTimeObj.isBefore(startTimeObj) || endTimeObj.isSame(startTimeObj)) {
            toast.error("Error", { description: "End time must be after start time." });
            return;
        }

        if (!startTimeObj.isSame(endTimeObj, 'day')) {
            toast.error("Error", { description: "Mehrtägige Blocker nicht erlaubt." });
            return;
        }

        const startTimeLocal = LocalDateTime.fromUTCDate(startTimeObj.utc().toDate(), 'Europe/Berlin');
        const endTimeLocal = LocalDateTime.fromUTCDate(endTimeObj.utc().toDate(), 'Europe/Berlin');
        if (dataType === 'practitioners') {
            const mutationData = {
                practitionerIds: selectedDataObjects.map((obj) => obj.id),
                storeId: selectedStore?.id,
                type: PractitionerScheduleEntryType.BLOCKED, // Fixed type
                blockedType: blockedType,
                description: description || undefined, // Send undefined if empty
                startTimeLocalSerialized: startTimeLocal.serialize(),
                endTimeLocalSerialized: endTimeLocal.serialize(),
                isRecurring,
                recurrenceRule: isRecurring ? derivedRecurrenceRule : undefined,
                recurrenceEndDate: isRecurring && recurrenceEndDateObj ? recurrenceEndDateObj.endOf('day').toISOString() : undefined, // Send end of day UTC
                detectedConflicts: serializeConflictsForLog(conflicts),
            };

            createPractitionerScheduleEntryMutation.mutate(mutationData);
        } else if (dataType === 'equipment') {
            const mutationData = {
                equipmentIds: selectedDataObjects.map((obj) => obj.id),
                type: EquipmentScheduleEntryType.BLOCKED, // Fixed type
                description: description || undefined, // Send undefined if empty
                startTimeLocalSerialized: startTimeLocal.serialize(),
                endTimeLocalSerialized: endTimeLocal.serialize(),
                isRecurring,
                recurrenceRule: isRecurring ? derivedRecurrenceRule : undefined,
                recurrenceEndDate: isRecurring && recurrenceEndDateObj ? recurrenceEndDateObj.endOf('day').toISOString() : undefined, // Send end of day UTC
            };

            createEquipmentScheduleEntryMutation.mutate(mutationData);
        }
    };

    const handleCancel = () => {
        setIsOpen(false);
    }

    const mutationLoading = createPractitionerScheduleEntryMutation.isPending || createEquipmentScheduleEntryMutation.isPending;

    const [conflicts, setConflicts] = useState<{
        hasConflicts: boolean;
        conflicts: Array<{
            appointmentId: string;
            appointmentTitle: string;
            customerName: string;
            practitionerId: number;
            practitionerName: string;
            stepStartSerialized: string;
            stepEndSerialized: string;
            blockerStartSerialized: string;
            blockerEndSerialized: string;
        }>;
        totalConflictsFound: number;
    } | null>(null);
    const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);

    const checkConflicts = useCallback(async () => {
        if (!selectedStore || selectedDataObjects.length === 0 || !startTimeObj || !endTimeObj) {
            setConflicts(null);
            return;
        }

        if (dataType === 'practitioners' && !blockedType) {
            setConflicts(null);
            return;
        }

        setIsCheckingConflicts(true);
        try {
            const result = await trpcUtils.appointments.checkScheduleEntryConflicts.fetch({
                practitionerIds: (selectedDataObjects as Practitioner[]).map(p => p.id),
                storeId: selectedStore.id,
                startTimeLocalSerialized: LocalDateTime.fromUTCDate(startTimeObj.utc().toDate(), 'Europe/Berlin').serialize(),
                endTimeLocalSerialized: LocalDateTime.fromUTCDate(endTimeObj.utc().toDate(), 'Europe/Berlin').serialize(),
                isRecurring,
                recurrenceRule: isRecurring ? derivedRecurrenceRule : undefined,
                recurrenceEndDate: isRecurring && recurrenceEndDateObj ? recurrenceEndDateObj.toISOString() : undefined,
            });
            if (result.hasConflicts && 'totalConflictsFound' in result) {
                setConflicts(result);
            } else {
                setConflicts(null);
            }
        } catch (error) {
            console.error('Error checking conflicts:', error);
            setConflicts(null);
        } finally {
            setIsCheckingConflicts(false);
        }
    }, [selectedStore, selectedDataObjects, startTimeObj, endTimeObj, dataType, blockedType, isRecurring, derivedRecurrenceRule, recurrenceEndDateObj, trpcUtils]);

    const debouncedConflictCheck = useDebounceFunction(checkConflicts, 500);

    useEffect(() => {
        debouncedConflictCheck();
    }, [debouncedConflictCheck]);

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                <Button variant='outline' {...triggerButtonProps}>
                    <Clock2 />
                    {triggerButtonLabel}
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"> {/* Adjust width as needed */}
                <DialogHeader>
                    <DialogTitle>Kalender blockieren</DialogTitle>
                    <DialogDescription>
                        Wähle einen {dataTypeDisplayName} und gib an, wann du seinen Kalender blockieren möchtest.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="type">Type</Label>
                        <Tabs value={dataType} onValueChange={(val) => canManageShiftEntries && setDataType(val as 'practitioners' | 'equipment')}>
                            <TabsList className="grid grid-cols-2">
                                <TabsTrigger value="practitioners" disabled={!canManageShiftEntries}>Behandler</TabsTrigger>
                                <TabsTrigger value="equipment" disabled={!canManageShiftEntries}>Geräte</TabsTrigger>
                            </TabsList>
                        </Tabs>
                    </div>

                    {/* Practitioner Search Combobox */}
                    <div className="space-y-2">
                        <Label htmlFor="practitioner-search">{`${dataTypeDisplayName} `}*</Label>
                        <Popover open={dataSearchOpen} onOpenChange={(open) => setDataSearchOpen(canManageShiftEntries ? open : false)}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={dataSearchOpen}
                                    className="w-full max-w-[400px] justify-between"
                                    disabled={!canManageShiftEntries || mutationLoading}
                                >
                                    <span className='truncate'>
                                        {dataObjectDisplayName
                                            ? dataObjectDisplayName
                                            : `${dataTypeDisplayName} auswählen...`}
                                    </span>
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start"> {/* Ensure width matches trigger */}
                                <Command shouldFilter={false} > {/* We control filtering via debounced API call */}
                                    <CommandInput
                                        id="practitioner-search"
                                        placeholder="Suchen..."
                                        value={dataSearchQuery}
                                        onValueChange={setDataSearchQuery} // Update search query state
                                        disabled={!canManageShiftEntries || mutationLoading}
                                    />
                                    <CommandList>
                                        {searchIsLoading && debouncedSearchQuery && <CommandEmpty>Loading...</CommandEmpty>}
                                        {!searchIsLoading && searchOptionsIsEmpty && debouncedSearchQuery && <CommandEmpty>No {dataType.toLowerCase()} found.</CommandEmpty>}
                                        {!debouncedSearchQuery && <CommandEmpty>Tippen zum Suchen...</CommandEmpty>}
                                        {!searchOptionsIsEmpty && (
                                            <CommandGroup>
                                                {(practitionerOptions.length) && (
                                                    <CommandItem
                                                        onSelect={() => {
                                                            if (selectedDataObjects.length > 0) {
                                                                setSelectedDataObjects([]);
                                                            } else {
                                                                handleDataObjectSelectAll();
                                                            }
                                                        }}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                selectedDataObjects.some((obj) => dataType === 'practitioners' ? selectedDataObjects.length === practitionerOptions.length : selectedDataObjects.length === equipmentOptions.length) ? "opacity-100" : "opacity-0"
                                                            )}
                                                        />
                                                        <div>
                                                            {selectedDataObjects.length > 0 ? 'Alle abwählen' : 'Alle auswählen'}
                                                        </div>
                                                    </CommandItem>
                                                )}
                                                {(practitionerOptions.length && dataType === 'practitioners') ? practitionerOptions.map((practitioner) => (
                                                    <CommandItem
                                                        key={practitioner.id}
                                                        value={`${practitioner.firstName} ${practitioner.lastName} ${practitioner.categoryName ?? ''} ${practitioner.id}`}
                                                        onSelect={() => handleDataObjectSelect(practitioner)}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                selectedDataObjects.some((obj) => obj.id === practitioner.id) ? "opacity-100" : "opacity-0"
                                                            )}
                                                        />
                                                        <PractitionerNameWithCategory
                                                            firstName={practitioner.firstName}
                                                            lastName={practitioner.lastName}
                                                            title={practitioner.title}
                                                            categoryName={practitioner.categoryName}
                                                        />
                                                    </CommandItem>
                                                )) : null}
                                                {(equipmentOptions.length && dataType === 'equipment') ? equipmentOptions.map((equipment) => (
                                                    <CommandItem
                                                        key={equipment.id}
                                                        value={`${equipment.name} ${equipment.id}`} // Unique value for selection
                                                        onSelect={() => handleDataObjectSelect(equipment)}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                selectedDataObjects.some((obj) => obj.id === equipment.id) ? "opacity-100" : "opacity-0"
                                                            )}
                                                        />
                                                        <div>
                                                            {equipment.name}
                                                        </div>
                                                    </CommandItem>
                                                )) : null}
                                            </CommandGroup>
                                        )}
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </div>


                    <div className="flex items-end gap-6">
                        <div className="flex gap-2">
                            <div className="space-y-2">
                                <Label htmlFor="start-date">Startdatum *</Label>
                                <Input
                                    id="start-date"
                                    type="date"
                                    className="w-[140px]"
                                    value={formatDate(startTimeObj)}
                                    onChange={(e) => setStartTimeObj(parseDate(e.target.value, startTimeObj, userTimezone))}
                                    required
                                    disabled={!canManageShiftEntries || mutationLoading}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="start-time">Startzeit *</Label>
                                <Input
                                    id="start-time"
                                    type="time"
                                    className="w-[100px]"
                                    value={formatTime(startTimeObj)}
                                    onChange={(e) => setStartTimeObj(parseTime(e.target.value, startTimeObj, userTimezone))}
                                    required
                                    disabled={!canManageShiftEntries || mutationLoading}
                                />
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <div className="space-y-2">
                                <Label htmlFor="end-date">Enddatum *</Label>
                                <Input
                                    id="end-date"
                                    type="date"
                                    className="w-[140px]"
                                    value={formatDate(endTimeObj)}
                                    onChange={(e) => setEndTimeObj(parseDate(e.target.value, endTimeObj, userTimezone))}
                                    required
                                    disabled={!canManageShiftEntries || mutationLoading}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="end-time">Endzeit *</Label>
                                <Input
                                    id="end-time"
                                    type="time"
                                    className="w-[100px]"
                                    value={formatTime(endTimeObj)}
                                    onChange={(e) => setEndTimeObj(parseTime(e.target.value, endTimeObj, userTimezone))}
                                    required
                                    disabled={!canManageShiftEntries || mutationLoading}
                                />
                            </div>
                        </div>
                    </div>
                    {startTimeObj && endTimeObj && !startTimeObj.isSame(endTimeObj, 'day') && (
                        <p className="text-sm text-destructive">Mehrtägige Blocker nicht erlaubt.</p>
                    )}

                    {dataType === 'practitioners' && (
                        <div className="space-y-2">
                            <Label htmlFor="blockedType">Blocker Grund</Label>
                            <Select
                                value={blockedType || undefined}
                                onValueChange={(value) => setBlockedType(value as PractitionerScheduleEntryBlockedType)}
                                disabled={!canManageShiftEntries || mutationLoading}
                            >
                                <SelectTrigger id="blockedType">
                                    <SelectValue placeholder="Select blocked type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={PractitionerScheduleEntryBlockedType.BREAK}>Pause</SelectItem>
                                    <SelectItem value={PractitionerScheduleEntryBlockedType.VACATION}>Urlaub</SelectItem>
                                    <SelectItem value={PractitionerScheduleEntryBlockedType.MEETING}>Meeting</SelectItem>
                                    <SelectItem value={PractitionerScheduleEntryBlockedType.SICK_LEAVE}>Krankheit</SelectItem>
                                    <SelectItem value={PractitionerScheduleEntryBlockedType.EDUCATION}>Fortbildung</SelectItem>
                                    <SelectItem value={PractitionerScheduleEntryBlockedType.OTHER}>Sonstiges</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Description */}
                    <div className="space-y-2">
                        <Label htmlFor="description">Beschreibung (optional)</Label>
                        <Textarea
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="z.B. Pause, Teammeeting"
                            disabled={!canManageShiftEntries || mutationLoading}
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <Switch
                            id="is-recurring"
                            checked={isRecurring}
                            onCheckedChange={(checked) => setIsRecurring(Boolean(checked))}
                            disabled={!canManageShiftEntries || mutationLoading}
                        />
                        <Label htmlFor="is-recurring" className="cursor-pointer">Wiederkehrend</Label>
                    </div>

                    {isRecurring && (
                        <div className="space-y-4 border rounded-md p-3 bg-muted/30">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Frequenz</Label>
                                    <Select
                                        value={frequency}
                                        onValueChange={(value: 'DAILY' | 'WEEKLY' | 'MONTHLY') => setFrequency(value)}
                                        disabled={!canManageShiftEntries || mutationLoading}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value='DAILY'>Täglich</SelectItem>
                                            <SelectItem value='WEEKLY'>Wöchentlich</SelectItem>
                                            <SelectItem value='MONTHLY'>Monatlich</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Intervall (alle x Tage/Wochen/Monate)</Label>
                                    <Input
                                        type="number"
                                        min={1}
                                        value={interval}
                                        onChange={(e) => setInterval(parseInt(e.target.value, 10) || 1)}
                                        disabled={!canManageShiftEntries || mutationLoading}
                                    />
                                </div>
                            </div>

                            {frequency === 'WEEKLY' && (
                                <div className="space-y-2">
                                    <Label>Wochentage</Label>
                                    <div className="flex gap-2 flex-wrap">
                                        {Object.entries(weekdays).map(([day, isSelected]) => (
                                            <label key={day} className="flex items-center gap-1 text-sm">
                                                <Checkbox
                                                    checked={isSelected}
                                                    onCheckedChange={(checked) =>
                                                        setWeekdays(prev => ({ ...prev, [day]: Boolean(checked) }))
                                                    }
                                                    disabled={!canManageShiftEntries || mutationLoading}
                                                />
                                                {{ MO: 'Mo', TU: 'Di', WE: 'Mi', TH: 'Do', FR: 'Fr', SA: 'Sa', SU: 'So' }[day]}
                                            </label>
                                        ))}
                                    </div>
                                    {!Object.values(weekdays).some(selected => selected) && (
                                        <p className="text-xs text-destructive">Bitte mindestens einen Tag auswählen.</p>
                                    )}
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label>Enddatum (optional)</Label>
                                <Input
                                    type="date"
                                    value={recurrenceEndDateObj?.format('YYYY-MM-DD') ?? ''}
                                    onChange={(e) => setRecurrenceEndDateObj(e.target.value ? dayjs(e.target.value).tz(userTimezone, true) : null)}
                                    disabled={!canManageShiftEntries || mutationLoading}
                                />
                            </div>
                        </div>
                    )}

                    {dataType === 'practitioners' && (
                        <>
                            {conflicts && conflicts.hasConflicts ? (
                                <div className="space-y-2">
                                    <Alert variant="destructive">
                                        <AlertTriangle className="h-4 w-4" />
                                        <AlertTitle>Kalenderkonflikte gefunden!</AlertTitle>
                                        <AlertDescription>
                                            <p className="mb-2">
                                                {conflicts.totalConflictsFound > conflicts.conflicts.length
                                                    ? `${conflicts.totalConflictsFound} Konflikte gefunden (erste ${conflicts.conflicts.length} angezeigt):`
                                                    : `${conflicts.conflicts.length} Konflikt(e) gefunden:`}
                                            </p>
                                            <ul className="list-disc pl-5 space-y-1 text-sm max-h-[200px] overflow-y-auto">
                                                {conflicts.conflicts.map((conflict, idx) => (
                                                    <li key={idx}>
                                                        <strong>{conflict.practitionerName}</strong>: Termin "{conflict.appointmentTitle}"
                                                        ({conflict.customerName}) am {LocalDateTime.deserialize(conflict.stepStartSerialized).format('dd.MM.yyyy HH:mm')}{' '}
                                                        überschneidet sich mit Blockierung ({LocalDateTime.deserialize(conflict.blockerStartSerialized).format('dd.MM.yyyy HH:mm')} - {LocalDateTime.deserialize(conflict.blockerEndSerialized).format('dd.MM.yyyy HH:mm')})
                                                    </li>
                                                ))}
                                            </ul>
                                        </AlertDescription>
                                    </Alert>
                                </div>
                            ) : !isCheckingConflicts ? (
                                <Alert className="border-green-500 bg-green-50 text-green-900">
                                    <Check className="h-4 w-4 text-green-600" />
                                    <AlertTitle className="text-green-900">Keine Konflikte erkannt</AlertTitle>
                                    <AlertDescription className="text-green-800">
                                        Keine Kalenderkonflikte erkannt.
                                    </AlertDescription>
                                </Alert>
                            ) : null}
                        </>
                    )}

                    <DialogFooter className="pt-4">
                        <Button type="button" variant="outline" onClick={handleCancel} disabled={mutationLoading}>
                            Abbrechen
                        </Button>
                        {canManageShiftEntries && (
                            <Button type="submit" disabled={mutationLoading || selectedDataObjects.length === 0 || (dataType === 'practitioners' && !blockedType)}>
                                {mutationLoading ? 'Speichern...' : 'Blockieren'}
                            </Button>
                        )}
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};